import { mapState, mapGetters } from 'vuex';
import { downloadAttachment } from '@/utils/vuex';
import ApiService from '@/services/apiService';
import iconsMixin from '../dtwin/state/bridge/iconsMixin';
import { get, isArray, cloneDeep } from 'lodash';
import MarkdownItVueLight from 'markdown-it-vue/dist/markdown-it-vue-light.umd.min.js';
import CSVParser from '@/utils/csvparser';
import devicesLimitMixin from './devicesLimitMixin';

// Maximum devices limit is 1000, but put 1001, because of the header in csv file
const MAXIMUM_DEVICES_IMPORT_THRESHOLD = 1001;
let BASIC_CREATION_FIELDS = [{
  name: 'Title',
  disabled: true,
  field: 'title',
  optional: false,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'string'
}, {
  name: 'Description',
  field: 'description',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'string'
}, {
  name: 'Perform Device Import',
  field: 'isDeviceImport',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'boolean',
  description: true
}, {
  name: 'Location',
  field: 'location',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'string'
}, {
  name: 'Coordinates',
  field: 'coordinates',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'array'
}, {
  name: 'Tags',
  field: 'tags',
  optional: true,
  usedInTemplate: true,
  canEditInTemplate: true,
  type: 'array'
}, {
  name: 'Device Driver',
  field: 'deviceDriver',
  optional: true,
  usedInTemplate: true,
  canEditInTemplate: false,
  type: 'number',
  description: true
}, {
  name: 'Device Type',
  field: 'deviceType',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'number'
}, {
  name: 'Groups',
  field: 'groups',
  optional: true,
  usedInTemplate: true,
  canEditInTemplate: true,
  type: 'array'
}, {
  name: 'Operational Status',
  field: 'operationalStatus',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'enum'
}, {
  name: 'Perform Address Lookup',
  field: 'performAddressLookup',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'boolean',
  description: true
}, {
  name: 'Perform Coordinate Lookup',
  field: 'performCoordinateLookup',
  optional: true,
  usedInTemplate: false,
  canEditInTemplate: false,
  type: 'boolean',
  description: true
}, {
  name: 'Icon',
  field: 'faIcon',
  optional: true,
  usedInTemplate: true,
  canEditInTemplate: false,
  type: 'string'
}, {
  name: 'Custom properties',
  field: 'custom_KEYNAME',
  optional: true,
  usedInTemplate: true,
  canEditInTemplate: true,
  type: 'string'
}, {
  name: 'Target Reference ID',
  field: 'targetReferenceId_KEYNAME',
  optional: true,
  usedInTemplate: true,
  canEditInTemplate: true,
  type: 'string'
}];
const CREATION_FIELDS_EXAMPLES = {
  title: '"Virtual Device Title"',
  description: 'Description',
  tags: '"tag1,tag2"',
  deviceDriver: 14,
  deviceType: 14,
  isDeviceImport: true,
  groups: '"group1,group2"',
  custom_KEYNAME: '"value"',
  targetReferenceId_KEYNAME: '"targetReference"',
  operationalStatus: 4,
  dev_eui: 'n2ks3apem5hah78f',
  join_eui: 'Join eui',
  app_s_key: 'App Server Key',
  device_id_thingsstack: 'n2ks3apem5hah78f',
  _LoRaWANCaps: 3,
  f_nwk_s_int_key: 'Network server key',
  lorawan_version: 4,
  activation_method: 0,
  lorawan_phy_version: 5,
  activation: 'otaa',
  security_mode: 'psk',
  dtlsPsk: '11FF',
  connection_server_type: 'management',
  device_id: '8E44E404A2A95FCD',
  address: '282D727C',
  location: '"Berlin, Germany"',
  coordinates: '"52.4607269,13.4127513"',
  application_key: '2F73CEA4F27658C52F73CEA4F27658C5',
  application_session_key: 'Application session key',
  network_session_key: 'Network session key',
  class_c: false,
  region: 'EU868',
  rx2_data_rate: 4,
  adr_limit: 2,
  deduplicate: true,
  skip_fcnt_check: false,
  dev_status_req: false,
  app_eui: '8E44E404A2A95FCD',
  app_key: 'F131E6B694C4DD1CDCE45557B4045FBA',
  dev_addr: 'D261CA59',
  nwks_key: '24A650DAAD4182C2033E766BDFBBFF1E',
  apps_key: 'BA27908EDA32F33FB17DDAC4D70C9B25',
  frequency_plan_id: 'EU_863_870_TTN',
  seq_no: 0,
  seq_dn: 0,
  performAddressLookup: true,
  performCoordinateLookup: true,
  faIcon: 'archway',
  Manufacturer: 'DCF429BF138A7A57FDEC0900E7C82844',
  IdentificationNumber: '12345678',
  key: '1A3F',
  base64: false
};
const FREQUENCY_PLANS_LINK = '`https://www.thethingsindustries.com/docs/reference/frequency-plans/`\n';
const dmsCoordinatesRegExp =
// ^\s*         any amount of leading whitespace
// (\d+)        capture the integer of degrees
// °            degree symbol
// \s*          any amount of whitespace
// (\d+)        capture the integer of minutes
// ['′]         single quote or prime
// \s*          any amount of whitespace
// (\d+\.?\d*)  capture the decimal of seconds
// (?:"|″|'')   double quote, double prime or two single quotes
// \s*          any amount of whitespace
// ([NESW])     capture N, S, E or W of the cardinal direction
// \s*$         any amount of trailing whitespace
/^\s*(\d+)°\s*(\d+)['′]\s*(\d*\.?\d*)(?:"|″|'')\s*([NESW])\s*$/;
const signedFloatNumberRegExp =
// ^\s*         any amount of leading whitespace
// [-+]?        optional sign
// \d+          one or more digits
// \.?          optional decimal point
// \d*          zero or more digits
// \s*$         any amount of trailing whitespace
/^\s*[+-]?\d+\.?\d*\s*$/;
export default {
  name: 'CoreVirtualDeviceBulkImport',
  components: {
    MarkdownItVueLight
  },
  mixins: [iconsMixin, devicesLimitMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      useTemplate: false,
      selectedTemplate: null,
      accountTemplatesData: {
        templates: [],
        currentPage: 1,
        hasNextPage: true
      },
      parentDTwin: {},
      title: '',
      selectedAccount: null,
      selectedBridge: null,
      deviceBridgesForAccount: [],
      deviceBridgesLoading: false,
      bridgeTypes: [],
      parentId: null,
      operationalStatus: null,
      selectedCreationFields: BASIC_CREATION_FIELDS,
      availableCreationFields: BASIC_CREATION_FIELDS,
      hasCsvUploaded: false,
      accounts: [],
      instructions: '',
      step: 1,
      contents: {
        devices: []
      },
      headers: [],
      validationErrors: [],
      createButtonDisabled: false,
      remainingDevices: 0,
      hasUnsupportedImportColumn: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),
    bridgeTypeMap() {
      const map = {};
      if (this.bridgeTypes) {
        this.bridgeTypes.forEach(type => {
          map[type.type] = type;
        });
      }
      return map;
    },
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    operationalStatuses() {
      return [{
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.unset'),
        value: 0
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.inTransit'),
        value: 1
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.readyForOperation'),
        value: 2
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.inOperation'),
        value: 3
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.temporaryInactive'),
        value: 4
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.decommissioned'),
        value: 5
      }];
    },
    isDeviceDriverActive() {
      return !this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback');
    },
    limitedDevices() {
      return this.deviceTypes.find(deviceType => this.hasDeviceLimit && this.selectedBridge && this.selectedBridge.type === deviceType.bridgeType);
    },
    deviceTypes() {
      return [{
        bridgeType: 'bridge-wireless-mbus-decoder',
        translationKey: 'DigitalTwin.VirtualDevice.bulkImport.remainingWmbusDevices'
      }, {
        bridgeType: 'bridge-lwm2m-server',
        translationKey: 'DigitalTwin.VirtualDevice.bulkImport.remainingLwm2mDevices'
      }];
    }
  },
  watch: {
    selectedAccount() {
      if (this.selectedAccount === null) {
        return;
      }
      this.fetchBridges();
      this.hasDeviceLimit = false;
      this.remainingDevices = 0;
      this.accountTemplatesData = {
        templates: [],
        currentPage: 1,
        hasNextPage: true
      };
      this.fetchNextTemplates();
    },
    selectedTemplate(newTemplate) {
      if (newTemplate && this.useTemplate) {
        this.selectedBridge = this.deviceBridgesForAccount.find(bridge => bridge.id === newTemplate.deviceMeta.connectorId);
        ApiService.digitalTwinGet(this, newTemplate.parentId).then(parentDTwin => {
          this.parentDTwin = parentDTwin;
          this.$nextTick(() => {
            this.updateSelectedBridgeFields();
          });
        }).catch(error => {
          var _error$response;
          const messageKey = (_error$response = error.response) === null || _error$response === void 0 || (_error$response = _error$response.data) === null || _error$response === void 0 ? void 0 : _error$response.message;
          if (messageKey && this.$i18n_t(`Error.${messageKey}`)) {
            this.$snack(this.$i18n_t(`Error.${messageKey}`), 'error');
          } else {
            this.$snack(this.$i18n_t('Common.digitalTwins.twinNotFound'), 'error');
          }
        });
      }
    },
    selectedBridge() {
      var _this$selectedBridge;
      this.hasDeviceLimit = false;
      this.remainingDevices = 0;
      this.updateSelectedBridgeFields();

      // Check limits if applicable
      const type = (_this$selectedBridge = this.selectedBridge) === null || _this$selectedBridge === void 0 ? void 0 : _this$selectedBridge.type;
      if (type === this.bridgeTypesMapping.wmbus) {
        this.checkLimits(type, this.moduleKeys.wmbus, this.limitExceededFlags.wmbus);
      } else if (type === this.bridgeTypesMapping.lwm2m) {
        this.checkLimits(type, this.moduleKeys.lwm2m, this.limitExceededFlags.lwm2m);
      }
    },
    useTemplate(newValue, oldValue) {
      // If selectedBridge is already set, re-trigger field calculation
      if (this.selectedBridge) {
        this.updateSelectedBridgeFields();
      }

      // If template was selected and now it's turned off, also reset selectedTemplate
      if (!newValue && oldValue && this.selectedTemplate) {
        this.selectedTemplate = null;
      }
    }
  },
  created() {
    this.fetchAccounts();
  },
  methods: {
    async endTemplatesIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        await this.fetchNextTemplates();
      }
    },
    async fetchNextTemplates() {
      if (!this.accountTemplatesData.hasNextPage || this.accountTemplatesData.isLoading) {
        return;
      }
      try {
        const TEMPLATE_PAGE_SIZE = 10;
        this.accountTemplatesData.isLoading = true;
        const moreTemplates = await ApiService.deviceTemplatesFetch({
          tenantIds: [this.selectedAccount],
          page: this.accountTemplatesData.currentPage,
          pageSize: TEMPLATE_PAGE_SIZE
        });
        this.accountTemplatesData.currentPage++;
        const pageProductiveData = moreTemplates.data.map(td => td.ProductiveVersion);
        this.accountTemplatesData.templates = [...this.accountTemplatesData.templates, ...pageProductiveData];
        if (this.accountTemplatesData.templates.length >= moreTemplates.pagination['total-count']) {
          this.accountTemplatesData.hasNextPage = false;
        }
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.fetchError'), 'error');
      } finally {
        this.accountTemplatesData.isLoading = false;
      }
    },
    setInstructions() {
      if (!this.selectedBridge) {
        return;
      }
      let instructions = `## ${this.$i18n_t('DigitalTwin.VirtualDevice.bulkImport.mandatoryFields')}:\n`;
      const config = this.getBridgeConfig(this.selectedBridge);
      BASIC_CREATION_FIELDS.forEach(field => {
        config[field.field] = field;
      });
      const sortedKeys = Object.keys(config).sort((a, b) => config[a].name.localeCompare(config[b].name));
      const sortedConfig = {};
      sortedKeys.forEach(key => {
        sortedConfig[key] = config[key];
      });
      let optionalInstructions = '';
      Object.keys(sortedConfig).forEach(entry => {
        if (sortedConfig[entry].optional) {
          optionalInstructions = this.appendToInstructions(optionalInstructions, sortedConfig[entry]);
        } else {
          instructions = this.appendToInstructions(instructions, sortedConfig[entry]);
        }
      });
      instructions += `------------\n## ${this.$i18n_t('DigitalTwin.VirtualDevice.bulkImport.optionalFields')}:\n`;
      instructions += optionalInstructions;
      this.instructions = instructions;
      return instructions;
    },
    applyChanges() {
      this.createButtonDisabled = true;
      this.contents.title = this.title;
      if (this.useTemplate) {
        this.contents.deviceTemplateId = this.selectedTemplate.id;
      }
      this.createRunner(this.contents);
    },
    createRunner(data) {
      ApiService.createBackgroundRunnerBulkImport(data).then(() => {
        this.createButtonDisabled = false;
        this.closeDialog();
        this.$snack(this.$i18n_tc('DigitalTwin.VirtualDevice.bulkImport.success', this.contents.devices.length, {
          n: this.contents.devices.length
        }), 'success');
        this.clearForm();
      }).catch(error => {
        this.createButtonDisabled = false;
        this.closeDialog();
        this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        this.clearForm();
      });
    },
    closeDialog() {
      this.step = 1;
      this.selectedAccount = null;
      this.selectedBridge = null;
      this.useTemplate = false;
      this.selectedTemplate = null;
      this.selectedCreationFields = BASIC_CREATION_FIELDS;
      this.availableCreationFields = BASIC_CREATION_FIELDS;
      this.instructions = '';
      this.parentId = null;
      this.parentDTwin = {};
      this.title = '';
      this.faIcon = '';
      this.hasCsvUploaded = false;
      this.validationErrors = [];
      this.hasUnsupportedImportColumn = false;
      this.$emit('close');
    },
    parentSelectorFilter(item) {
      return (
        // only allow new parents from the same tenant
        item.tenantId === this.selectedAccount
      );
    },
    fetchAccounts() {
      this.$store.dispatch('app/loadAccounts').then(accounts => {
        // filter the root-tenant, because that one does not have a twin to attach the new device to
        this.accounts = accounts.filter(t => !t.isRootTenant);
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
      });
    },
    fetchBridges() {
      this.deviceBridgesForAccount = [];
      this.selectedBridge = null;
      this.deviceBridgesLoading = true;
      Promise.all([ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes.filter(type => {
          return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0;
        });
      }), ApiService.getBridgesForTenant(this.selectedAccount), ApiService.digitalTwinFetchChildren(this, null, 0, 1, null, ['bare'], false, this.selectedAccount)]).then(results => {
        this.bridgeTypes = results[0];
        let validDeviceBridges = results[1].filter(bridgeInstance => {
          const bridgeTypeDef = results[0].find(bridgeDef => bridgeDef.type === bridgeInstance.type);
          return get(bridgeTypeDef, 'manifest.functionScope.extension.devices.create') || get(bridgeTypeDef, 'manifest.functionScope.extension.devices.read');
        });
        this.deviceBridgesForAccount = validDeviceBridges.map(bridge => {
          return bridge.state === 'active' ? bridge : {
            ...bridge,
            name: `${bridge.name} (inactive)`,
            disabled: true
          };
        });
        this.parentId = get(results[2], 'digitaltwins[0].id', null);
        this.deviceBridgesLoading = false;
      }).catch(error => {
        this.deviceBridgesLoading = false;
        console.error('Error fetching bridges', error);
      });
    },
    bridgeConfig(bridge) {
      if (!bridge) {
        return;
      }
      return this.bridgeTypeMap[bridge.type];
    },
    getBridgeConfig(bridge) {
      const createDeviceMethod = get(this.bridgeConfig(bridge), 'manifest.functionScope.extension.devices.create');
      const result = get(this.bridgeConfig(bridge), `manifest.commands.instance.${createDeviceMethod}.parameters.data.properties`);
      return result;
    },
    bridgeReadConfig(bridge) {
      const selectedBridgeConfig = this.bridgeConfig(bridge);
      const readDeviceMethod = get(selectedBridgeConfig, 'manifest.functionScope.extension.devices.read');
      return get(selectedBridgeConfig, `manifest.commands.instance.${readDeviceMethod}`, null);
    },
    clearForm() {
      this.headers = [];
      this.contents = {
        devices: []
      };
      this.validationErrors = [];
    },
    constructValidateObject(config) {
      const validations = {};

      // eslint-disable-next-line max-statements
      Object.keys(config).forEach(field => {
        const property = config[field];
        let baseValidator = () => true;
        if (field === 'application_key') {
          baseValidator = value => typeof value === 'string' && value.length === 32;
          validations[field] = (value, row) => row.activation === 'otaa' ? baseValidator(value) : true;
          return;
        }
        if (field === 'key') {
          baseValidator = value => {
            const regExp = new RegExp('^[0-9A-Fa-f]+$', 'g');
            const trimmedValue = (value || '').trim();
            const isHexValid = regExp.exec(trimmedValue) !== null;
            const isLengthEven = trimmedValue.length % 2 === 0;
            return typeof value === 'string' && isHexValid && isLengthEven;
          };
          validations[field] = (value, row) => row.security_mode === 'psk' ? baseValidator(value) : true;
          return;
        }
        if (field === 'application_session_key' || field === 'network_session_key') {
          baseValidator = value => typeof value === 'string' && value.length === 32;
          validations[field] = (value, row) => row.activation === 'abp' ? baseValidator(value) : true;
          return;
        }
        if (field === 'deviceDriver') {
          baseValidator = value => typeof value === 'number';
        } else if (field === 'operationalStatus') {
          baseValidator = value => typeof value === 'number' && value >= 0 && value <= 5;
        } else if (field === 'coordinates') {
          baseValidator = value => value && value.length === 2 && this.validateCoordinates(value);
        } else if (property.type === 'string') {
          if (property.stringOptions && property.stringOptions.field === 'eui' && property.stringOptions.euiLength) {
            const euiLength = property.stringOptions.euiLength;
            baseValidator = value => {
              const regex = new RegExp(`^[0-9A-F]{${euiLength}}$`, 'gi');
              const trimmed = value.trim();
              const isValid = !!trimmed && regex.test(trimmed);
              if (property.stringOptions.upperCase) {
                return isValid && trimmed === trimmed.toUpperCase();
              }
              if (property.stringOptions.lowerCase) {
                return isValid && trimmed === trimmed.toLowerCase();
              }
              return isValid;
            };
          } else {
            baseValidator = value => typeof value === 'string';
          }
        } else if (property.type === 'array') {
          baseValidator = value => Array.isArray(value);
        } else if (property.type === 'enum' && property.field !== 'operationalStatus') {
          baseValidator = value => {
            return (property.values || []).map(v => v.toString()).includes(value.toString());
          };
        } else if (property.type === 'boolean') {
          baseValidator = value => value === true || value === false;
        }
        if (property.visible && typeof property.visible === 'object') {
          const [depField, depValue] = Object.entries(property.visible)[0];
          validations[field] = (value, fullRow) => {
            return fullRow[depField] === depValue ? baseValidator(value) : true;
          };
        } else {
          validations[field] = value => baseValidator(value);
        }
      });
      return validations;
    },
    // eslint-disable-next-line max-statements
    validateMandatoryFields(csv_parser, config) {
      var _this$selectedBridge2;
      const headers = new Set(csv_parser.getHeaders());
      headers.add('title'); // Ensure 'title' is always required

      const missingFields = new Set();

      // Statically required fields
      for (const [field, options] of Object.entries(config)) {
        let isStaticRequired = false;
        if (!this.useTemplate) {
          isStaticRequired = !options.optional;
        } else {
          isStaticRequired = !options.predefineInTemplates && !options.optional;
        }
        if (isStaticRequired && !headers.has(field)) {
          missingFields.add(field);
        }
      }

      // Conditionally required fields based on bridge type
      const conditionalChecks = {
        'bridge-firefly': row => {
          var _this$selectedTemplat, _this$selectedTemplat2;
          if ((row.activation === 'otaa' || ((_this$selectedTemplat = this.selectedTemplate) === null || _this$selectedTemplat === void 0 || (_this$selectedTemplat = _this$selectedTemplat.deviceMeta) === null || _this$selectedTemplat === void 0 || (_this$selectedTemplat = _this$selectedTemplat.connectorData) === null || _this$selectedTemplat === void 0 ? void 0 : _this$selectedTemplat.activation) === 'otaa') && !headers.has('application_key')) {
            missingFields.add('application_key');
          }
          if (row.activation === 'abp' || ((_this$selectedTemplat2 = this.selectedTemplate) === null || _this$selectedTemplat2 === void 0 || (_this$selectedTemplat2 = _this$selectedTemplat2.deviceMeta) === null || _this$selectedTemplat2 === void 0 || (_this$selectedTemplat2 = _this$selectedTemplat2.connectorData) === null || _this$selectedTemplat2 === void 0 ? void 0 : _this$selectedTemplat2.activation) === 'abp') {
            if (!headers.has('application_session_key')) {
              missingFields.add('application_session_key');
            }
            if (!headers.has('network_session_key')) {
              missingFields.add('network_session_key');
            }
          }
        },
        'bridge-lwm2m-server': row => {
          var _this$selectedTemplat3;
          if ((row.security_mode === 'psk' || ((_this$selectedTemplat3 = this.selectedTemplate) === null || _this$selectedTemplat3 === void 0 || (_this$selectedTemplat3 = _this$selectedTemplat3.deviceMeta) === null || _this$selectedTemplat3 === void 0 || (_this$selectedTemplat3 = _this$selectedTemplat3.connectorData) === null || _this$selectedTemplat3 === void 0 ? void 0 : _this$selectedTemplat3.security_mode) === 'psk') && !headers.has('key')) {
            missingFields.add('key');
          }
        }
      };
      const checker = conditionalChecks[(_this$selectedBridge2 = this.selectedBridge) === null || _this$selectedBridge2 === void 0 ? void 0 : _this$selectedBridge2.type];
      if (checker) {
        const parser = Object.create(csv_parser);
        parser.currentLineIndex = 1;
        const lines = parser.getNumberOfLines();
        for (let i = 0; i < lines; i++) {
          const {
            data: row
          } = parser.parseNextLine() || {};
          if (row) {
            checker(row);
          }
        }
      }
      return [...missingFields].join(', ');
    },
    validateCoordinates(value) {
      const [lat, long] = value;
      return (dmsCoordinatesRegExp.test(lat) || signedFloatNumberRegExp.test(lat) && Math.abs(parseFloat(lat)) < 360) && (dmsCoordinatesRegExp.test(long) || signedFloatNumberRegExp.test(long) && Math.abs(parseFloat(long)) < 360);
    },
    uploadeFile(csv) {
      this.clearForm();
      this.contents.parentId = this.parentId;
      this.contents.tenantId = this.selectedAccount;
      this.contents.bridgeId = this.selectedBridge.id;
      this.contents.bridgeType = this.selectedBridge.type;
      const config = this.getBridgeConfig(this.selectedBridge);
      const validationOptions = this.constructValidateObject(config);
      let options = {
        autoTyping: true,
        replaceHeader: {
          // id: 'identifier',
        },
        transform: {
          title: value => value.toString(),
          device_id: value => value.toString(),
          adr_limit: value => value.toString(),
          rx2_data_rate: value => value.toString(),
          IdentificationNumber: value => value.toString(),
          key: value => value.toString(),
          tags: value => value ? value.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
          groups: value => value ? value.split(',').map(group => group.trim()).filter(group => group) : [],
          coordinates: value => value ? value.split(',').map(coord => coord.trim()).map(coord => parseFloat(coord)).filter(coord => coord) : []
        },
        calculatedColumn: [],
        validate: validationOptions
      };
      if (this.selectedBridge.type === 'bridge-wireless-mbus-decoder') {
        options.calculatedColumn.push(rowData => {
          rowData['device_id'] = `${rowData['Manufacturer']}_${rowData['IdentificationNumber']}`;
        });
      }
      const csv_parser = new CSVParser(csv, options);
      if (csv_parser.getNumberOfLines() > MAXIMUM_DEVICES_IMPORT_THRESHOLD) {
        return this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.bulkImport.maximumLimitExceeded'), 'error');
      }
      const missingMandatoryFields = this.validateMandatoryFields(csv_parser, config);
      if (missingMandatoryFields) {
        return this.$snack(`Missing mandatory fields: ${missingMandatoryFields}`, 'error');
      }
      const result = csv_parser.parse();
      this.hasCsvUploaded = true;
      this.validationErrors = result.errors;
      this.contents.devices = result.data;
      this.headers = csv_parser.getHeaders().map(header => {
        return {
          sortable: false,
          text: header,
          value: header
        };
      });
      if (this.useTemplate) {
        this.appendTemplateData(this.headers, this.contents.devices, this.selectedTemplate);
      }
      if (!this.bridgeReadConfig(this.selectedBridge) && this.headers.some(h => h.value === 'isDeviceImport')) {
        this.hasUnsupportedImportColumn = true;
      }
    },
    appendTemplateData(headers, contentDevices, template) {
      const templateTags = template.deviceMeta.tags;
      const templateGroups = template.deviceMeta.groups;
      const templateCustomProperties = template.deviceMeta.customProperties;
      const templateReferenceIds = template.deviceMeta.referenceIds || {};
      const headersToAdd = [{
        value: 'deviceDriver',
        text: 'Device Driver'
      }, {
        value: 'icon',
        text: 'Icon'
      }];
      if (templateTags.length && !headers.some(h => h.value === 'tags')) {
        headersToAdd.push({
          text: 'Tags',
          value: 'tags'
        });
      }
      if (templateGroups.length && !headers.some(h => h.value === 'groups')) {
        headersToAdd.push({
          text: 'Groups',
          value: 'groups'
        });
      }
      const templateCustomPropertiesKeys = Object.keys(templateCustomProperties);
      const mandatoryCustomPropsKeys = [];
      templateCustomPropertiesKeys.forEach(cpKey => {
        if (templateCustomProperties[cpKey].requiredValue) {
          mandatoryCustomPropsKeys.push(cpKey);
        }
      });
      if (mandatoryCustomPropsKeys.length) {
        mandatoryCustomPropsKeys.forEach(cpKey => {
          if (!headers.some(h => h.value === `custom_${cpKey}`)) {
            headersToAdd.push({
              text: `Custom ${cpKey}`,
              value: `custom_${cpKey}`
            });
          }
        });
      }
      const templateReferenceIdsKeys = Object.keys(templateReferenceIds);
      const mandatoryReferenceIdsKeys = [];
      templateReferenceIdsKeys.forEach(key => {
        if (templateReferenceIds[key].requiredValue) {
          mandatoryReferenceIdsKeys.push(key);
        }
      });
      if (mandatoryReferenceIdsKeys.length) {
        mandatoryReferenceIdsKeys.forEach(key => {
          if (!headers.some(h => h.value === `targetReferenceId_${key}`)) {
            headersToAdd.push({
              text: `Reference ${key}`,
              value: `targetReferenceId_${key}`
            });
          }
        });
      }
      // if some data is not present within CSV but exist in template, add it
      headersToAdd.forEach(hta => {
        headers.unshift({
          sortable: false,
          text: hta.text,
          value: hta.value
        });
      });
      const templatedFields = ['deviceDriver', 'icon', 'tags', 'groups'];
      mandatoryCustomPropsKeys.forEach(cpKey => {
        templatedFields.push(`custom_${cpKey}`);
      });
      mandatoryReferenceIdsKeys.forEach(key => {
        templatedFields.push(`targetReferenceId_${key}`);
      });
      headers.forEach(header => {
        if (templatedFields.includes(header.value)) {
          header.icon = 'fa-sitemap';
        }
      });

      // add data from template to csv rows
      contentDevices.forEach(deviceRow => {
        deviceRow['deviceDriver'] = this.selectedTemplate.devicedriver.title;
        deviceRow['icon'] = template.deviceMeta.faIcon;
        if (templateTags.length) {
          if (deviceRow['tags']) {
            templateTags.forEach(tag => {
              if (!deviceRow['tags'].includes(tag)) {
                deviceRow['tags'].push(tag);
              }
            });
          } else {
            deviceRow['tags'] = templateTags;
          }
        }
        if (templateGroups.length) {
          if (deviceRow['groups']) {
            templateGroups.forEach(group => {
              if (!deviceRow['groups'].includes(group)) {
                deviceRow['groups'].push(group);
              }
            });
          } else {
            deviceRow['groups'] = templateGroups;
          }
        }
        if (mandatoryCustomPropsKeys.length) {
          mandatoryCustomPropsKeys.forEach(cpKey => {
            const keyName = `custom_${cpKey}`;
            if (!deviceRow[keyName]) {
              deviceRow[keyName] = templateCustomProperties[cpKey].defaultValue;
            }
          });
        }
        if (mandatoryReferenceIdsKeys.length) {
          mandatoryReferenceIdsKeys.forEach(cpKey => {
            const keyName = `targetReferenceId_${cpKey}`;
            if (!deviceRow[keyName]) {
              deviceRow[keyName] = templateReferenceIds[cpKey].defaultValue;
            }
          });
        }
      });
    },
    appendToInstructions(instructions, entry) {
      const bulkImportTranslation = this.$i18n_t('DigitalTwin.VirtualDevice.bulkImport');
      instructions += `### ${entry.name}\n`;
      if (entry.description) {
        instructions += `##### ${this.$i18n_t(`DigitalTwin.VirtualDevice.bulkImport.${entry.field}Description`)}\n`;
      }
      if (entry.outputValues || entry.values) {
        const values = entry.outputValues || entry.values;
        instructions += `#### ${bulkImportTranslation.possibleValues}:\n`;
        if (isArray(values)) {
          values.forEach(value => {
            instructions += `* ${value}\n`;
          });
        } else {
          Object.entries(values).forEach(([key, value]) => {
            instructions += `* ${key} : ${value}\n`;
          });
        }
      } else if (entry.type === 'boolean') {
        instructions += `#### ${bulkImportTranslation.possibleValues}:\n`;
        instructions += '* true\n* false\n';
      } else if (entry.field === 'frequency_plan_id') {
        instructions += `#### ${bulkImportTranslation.possibleValues}:\n${this.$i18n_t('DigitalTwin.VirtualDevice.bulkImport.seeHere')}: `;
        instructions += FREQUENCY_PLANS_LINK;
      } else if (entry.field === 'operationalStatus') {
        instructions += `#### ${bulkImportTranslation.possibleValues}:\n`;
        this.operationalStatuses.forEach(({
          value,
          text
        }) => {
          instructions += `* ${value} : ${text}\n`;
        });
      }
      instructions += `##### ${bulkImportTranslation.type}: ${entry.type}\n`;
      if (entry.stringOptions) {
        instructions += `##### ${bulkImportTranslation.required}:\n`;
        Object.entries(entry.stringOptions).forEach(([option, description]) => {
          instructions += `- ${option}: ${description}\n`;
        });
      }
      if (entry.default) {
        instructions += `##### ${bulkImportTranslation.default}: ${entry.default}\n`;
      }
      return instructions;
    },
    downloadSampleCsv() {
      const dataArray = [];
      const csvTitleCols = this.selectedCreationFields.map(cf => {
        dataArray.push(CREATION_FIELDS_EXAMPLES[cf.field]);
        return cf.field;
      });
      if (this.useTemplate) {
        // add required custom properties from template to csv sample
        const customProperties = this.selectedTemplate.deviceMeta.customProperties;
        const customPropertiesKeys = Object.keys(customProperties);
        if (this.useTemplate && customPropertiesKeys.length) {
          customPropertiesKeys.forEach(cpKey => {
            if (customProperties[cpKey].requiredValue) {
              csvTitleCols.push(`custom_${cpKey}`);
              dataArray.push(customProperties[cpKey].defaultValue);
            }
          });
        }
        const referenceIds = this.selectedTemplate.deviceMeta.referenceIds || {};
        const referenceIdsKeys = Object.keys(referenceIds);
        if (this.useTemplate && referenceIdsKeys.length) {
          referenceIdsKeys.forEach(key => {
            if (referenceIds[key].requiredValue) {
              csvTitleCols.push(`targetReferenceId_${key}`);
              dataArray.push(referenceIds[key].defaultValue);
            }
          });
        }
      }
      const colSeparator = ',';
      const csvData = [`${csvTitleCols.join(colSeparator)}`, `${[dataArray].join(colSeparator)}`].join('\n');
      const fileName = 'import-virtual-devices-sample.csv';
      downloadAttachment('text/plain;charset=utf-8', csvData, fileName);
    },
    updateSelectedBridgeFields() {
      if (!this.selectedBridge) {
        return;
      }
      const config = this.getBridgeConfig(this.selectedBridge);
      let baseFields = cloneDeep(BASIC_CREATION_FIELDS);
      if (this.isDeviceDriverActive) {
        baseFields = baseFields.filter(field => field.field !== 'deviceType');
      } else {
        baseFields = baseFields.filter(field => field.field !== 'deviceDriver');
      }
      let arr = [...baseFields];
      Object.entries(config).forEach(([key, value]) => {
        const newField = {
          ...value,
          field: key
        };
        const isVisible = value.visible && typeof value.visible === 'object';
        if (isVisible) {
          const [depField, depValue] = Object.entries(value.visible)[0];
          const isMatch = this.useTemplate ? get(this.selectedTemplate, `deviceMeta.connectorData.${depField}`) === depValue : false;
          if (isMatch) {
            newField.optional = false;
          }
        }
        if (newField.predefineInTemplates) {
          newField.usedInTemplate = true;
        }
        if (!newField.optional && (!this.useTemplate || !newField.usedInTemplate)) {
          newField.disabled = true;
        }
        arr.push(newField);
      });
      const seen = new Set();
      arr = arr.filter(f => {
        if (seen.has(f.field)) {
          return false;
        }
        seen.add(f.field);
        return true;
      });
      if (this.useTemplate) {
        arr.forEach(f => {
          if (f.usedInTemplate && !f.canEditInTemplate) {
            f.disabled = true;
          }
        });
      }
      this.availableCreationFields = arr;
      this.selectedCreationFields = arr.filter(f => f.disabled);
      if (this.useTemplate) {
        this.selectedCreationFields = this.selectedCreationFields.filter(f => !f.usedInTemplate);
      }
      if (!this.bridgeReadConfig(this.selectedBridge)) {
        this.availableCreationFields = this.availableCreationFields.filter(f => f.field !== 'isDeviceImport');
      }
      this.setInstructions();
    }
  }
};